import { Component, OnDestroy, computed, effect, inject, signal } from '@angular/core';

import { ConciergeService } from './concierge.service';

/**
 * Persistent amber bar shown at the top of every page during a read-only
 * impersonation. Shows the employer, "Read Only", and a live countdown to the
 * token's expiry. Exit restores the admin's session; on expiry it auto-exits.
 */
@Component({
  selector: 'app-concierge-banner',
  standalone: true,
  template: `
    @if (concierge.active()) {
      <div class="fixed top-0 inset-x-0 z-[2000] bg-amber-400 text-amber-950 shadow-md">
        <div class="mx-auto max-w-7xl px-4 py-2 flex items-center justify-between gap-3 text-sm">
          <span class="font-bold flex items-center gap-2 min-w-0">
            <i class="pi pi-eye shrink-0"></i>
            <span class="truncate">
              Concierge Mode · {{ concierge.employerName() || 'Employer' }} · Read Only · Expires in {{ countdown() }}
            </span>
          </span>
          <button type="button" (click)="exit()"
                  class="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-950 text-amber-50 hover:bg-amber-900 transition-colors">
            <i class="pi pi-sign-out text-[10px]"></i> Exit
          </button>
        </div>
      </div>
    }
  `
})
export class ConciergeBannerComponent implements OnDestroy {
  protected readonly concierge = inject(ConciergeService);

  /** Reserve space for the fixed bar while active so it doesn't overlap content. */
  private readonly _spacer = effect(() => {
    document.body.style.paddingTop = this.concierge.active() ? '2.5rem' : '';
  });

  /** Ticks every second so the countdown re-renders. */
  private readonly now = signal(Date.now());
  private readonly timer = setInterval(() => {
    this.now.set(Date.now());
    // Auto-exit the moment the impersonation token expires.
    if (this.concierge.active() && this.remainingMs() <= 0) {
      this.concierge.exit();
    }
  }, 1000);

  /** Milliseconds left on the impersonation token; 0 when unknown/expired. */
  private remainingMs(): number {
    const exp = this.concierge.expiresAt();
    if (!exp) { return 0; }
    const ms = new Date(exp).getTime() - this.now();
    return Number.isNaN(ms) ? 0 : ms;
  }

  protected readonly countdown = computed<string>(() => {
    const total = Math.floor(Math.max(0, this.remainingMs()) / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  });

  protected exit(): void { this.concierge.exit(); }

  ngOnDestroy(): void {
    clearInterval(this.timer);
    document.body.style.paddingTop = '';
  }
}
